import {
	type KeyboardEvent,
	type KeyboardEventHandler,
	type Ref,
	type RefObject,
	useRef,
} from 'react';

import Link from 'components/Link';
import Text from 'components/Text';
import type { FieldKey } from 'hooks';
import type { MainLinkFields } from 'models/sitecore';
import type { ColorString, TemplateString } from 'types';
import { cn } from 'utils/classNames';
import { formatTemplate } from 'utils/format';

import { getLinkListId, SCROLL_LOCK_WHITELIST_CLASS } from './helpers';
import PageHeaderLinkListItem, {
	type DropdownLinkClickHandler,
	type LinkListItemTabHandler,
} from './PageHeaderLinkListItem';
import PopularSearchTerms from './PopularSearchTerms';

export type LinkListTabHandler = (
	level: number,
	event: KeyboardEvent<HTMLAnchorElement>,
) => void;

interface Props {
	baseId: string;
	className?: string;
	firstLinkRef?: RefObject<HTMLAnchorElement | null>;
	hasPopularContent?: boolean;
	isLargeScreen: boolean | undefined;
	level: number;
	linkBarColor?: ColorString;
	links?: MainLinkFields[];
	navigationCategoryShowAllButton: TemplateString | FieldKey;
	onFirstLinkTab?: LinkListTabHandler;
	onLastLinkTab?: LinkListTabHandler;
	onLinkClick?: DropdownLinkClickHandler;
	onSelectedLinkTab?: LinkListTabHandler;
	openLevels: number;
	parentId?: string;
	ref?: Ref<HTMLDivElement>;
	renderPopularSearchTerms?: boolean;
	searchPopularSearchTermsText: string;
	selectedLink?: MainLinkFields;
	selectedLinkRef?: RefObject<HTMLAnchorElement | null>;
	selectedSiblingLinkRef?: RefObject<HTMLAnchorElement | null>;
	title?: string;
	viewAllHref?: string;
}

/** A single level of links in the dropdown tree */
export default function PageHeaderLinkList({
	baseId,
	className,
	firstLinkRef,
	hasPopularContent,
	isLargeScreen,
	level,
	linkBarColor,
	links,
	navigationCategoryShowAllButton,
	onFirstLinkTab,
	onLastLinkTab,
	onLinkClick,
	onSelectedLinkTab,
	openLevels,
	parentId,
	ref,
	renderPopularSearchTerms = false,
	searchPopularSearchTermsText,
	selectedLink,
	selectedLinkRef,
	selectedSiblingLinkRef,
	title,
	viewAllHref,
}: Props) {
	const lastPopularSearchTermRef = useRef<HTMLAnchorElement>(null);
	const linkCount = links?.length ?? 0;
	const selectedLinkIndex = links
		? links.findIndex((link) => link.id === selectedLink?.id)
		: -1;

	const handleLinkTab: LinkListItemTabHandler = (linkId, linkLevel, e) => {
		const linkIndex = links
			? links?.findIndex((link) => link.id === linkId)
			: -1;
		const isSelectedLink = linkId === selectedLink?.id;
		if (isSelectedLink && onSelectedLinkTab) {
			onSelectedLinkTab(linkLevel, e);
		}
		if (linkIndex === 0 && onFirstLinkTab) {
			onFirstLinkTab(linkLevel, e);
		}
		if (
			// Has no view all link
			!viewAllHref &&
			// Has no popular search terms
			!lastPopularSearchTermRef.current &&
			// Give the selected handler priority
			!isSelectedLink &&
			linkIndex === linkCount - 1 &&
			onLastLinkTab
		) {
			onLastLinkTab(linkLevel, e);
		}
	};

	const handleViewAllKeydown: KeyboardEventHandler<HTMLAnchorElement> = (e) => {
		if (
			e.key === 'Tab' &&
			!e.shiftKey &&
			// Only relevant when there are no popular search terms.
			!lastPopularSearchTermRef.current &&
			onLastLinkTab &&
			!hasPopularContent
		) {
			onLastLinkTab(level, e);
		}
	};

	const handleLastSearchTermTab: KeyboardEventHandler<HTMLAnchorElement> = (
		e,
	) => {
		if (onLastLinkTab) {
			onLastLinkTab(level, e);
		}
	};

	const hasViewAll = level > 1 && viewAllHref;
	const hasColumns = linkCount >= 12 && level >= 3;

	// Count + 1 to account for the 'view all' link that's added outside
	// the looped list of links and an additional + 1 to have more weight
	// on the first column.
	const rowCount = Math.ceil((linkCount + 2) / 2);
	const gridRowStyle = hasColumns
		? { gridTemplateRows: `repeat(${rowCount}, minmax(0, min-content))` }
		: undefined;

	const isOpen = isLargeScreen
		? level === 1
			? true
			: openLevels >= level
		: level === openLevels;

	return (
		<div
			ref={ref}
			id={`${baseId}-${level}`}
			// Escape hatch for focus management.
			data-open={String(isOpen)}
			className={cn(
				className,
				SCROLL_LOCK_WHITELIST_CLASS,
				'z-20 bg-base pb-7',
				!isOpen && 'invisible',
				!isLargeScreen && [
					'absolute top-0 bottom-0 w-full overscroll-contain transition-[visibility,opacity,translate] duration-150',
					!isOpen && 'delay-[150ms,0s,0s]',
					level === openLevels && 'translate-x-0 overflow-y-auto',
					level > openLevels && 'translate-x-5 opacity-0',
					level < openLevels && '-translate-x-5',
				],
				isLargeScreen && [
					level === 1 && 'md:bg-subtle md:forced-colors:border-r',
					// Divider line via a pseudo element.
					level === 3 && [
						'relative',
						'before:absolute',
						'before:top-0',
						'before:-left-3',
						'before:block',
						'before:h-full',
						'before:border-r',
						'before:border-r-subtle',
					],
				],
			)}
		>
			{title && (
				<Text
					as={(['h2', 'h3', 'h4'] as const)[level - 1] ?? 'h2'}
					styleAs={level > 1 ? 'h3' : undefined}
					className="mt-8 min-h-9 px-8 md:mt-10 md:min-h-12 md:px-6"
				>
					{title}
				</Text>
			)}
			{links && (
				<ul
					id={getLinkListId(baseId, parentId)}
					style={gridRowStyle}
					className={cn(
						!title && 'pt-7',
						hasColumns && 'grid-flow-col grid-cols-2 md:grid',
					)}
				>
					{links.map((link, i) => (
						<li
							key={link.id}
							aria-owns={
								link.id === selectedLink?.id
									? getLinkListId(baseId, selectedLink.id)
									: undefined
							}
						>
							<PageHeaderLinkListItem
								baseId={baseId}
								isSelected={link.id === selectedLink?.id}
								level={level}
								link={link}
								linkBarColor={linkBarColor}
								onClick={onLinkClick}
								onTab={handleLinkTab}
								ref={
									link.id === selectedLink?.id
										? selectedLinkRef
										: selectedLinkIndex !== -1 && i === selectedLinkIndex + 1
											? selectedSiblingLinkRef
											: i === 0
												? firstLinkRef
												: undefined
								}
							/>
						</li>
					))}
					{hasViewAll && (
						<li>
							<Link
								href={viewAllHref}
								ref={
									selectedLinkIndex === linkCount - 1
										? selectedSiblingLinkRef
										: undefined
								}
								className="flex min-h-12 items-center px-8 text-md md:min-h-10 md:px-6"
								onKeyDown={handleViewAllKeydown}
								underline
							>
								{formatTemplate(navigationCategoryShowAllButton, {
									category: title,
								})}
							</Link>
						</li>
					)}
				</ul>
			)}
			{renderPopularSearchTerms && (
				<PopularSearchTerms
					heading={searchPopularSearchTermsText}
					variant="chips"
					itemLimit={5}
					lastLinkRef={lastPopularSearchTermRef}
					onLastLinkTab={handleLastSearchTermTab}
					className="p-8"
				/>
			)}
		</div>
	);
}
PageHeaderLinkList.displayName = 'PageHeaderLinkList';
